import {
  Box,
  ColorSwatch,
  Flex,
  Group,
  HoverCard,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { PieChart, Pie, Cell, PieLabel } from "recharts";
import { useMediaQuery } from "@mantine/hooks";
import {
  DocumentControlIcon,
  ManagingContentIcon,
  MarketingIcon,
  ProjectManagmentIcon,
} from "../../../shared/svg";
import { LikeResponse } from "../../../entities/components/LikeResponse";
import useReportStore from "../../../store/useReportStore";

const COLORS = ["#05D8D2", "#A64DD7", "#6F40E2", "#3A8BD9"];
const RADIAN = Math.PI / 180;

const generateData = (
  keyNumbers: {
    title: string;
    description: string;
  }[]
) => {
  return keyNumbers.map((item) => ({
    name: item.title,
    value: 100,
  }));
};

export const CareerInNumbers = () => {
  const { report, reportId } = useReportStore();
  const matches = useMediaQuery("(max-width: 1100px)");

  const renderCustomizedLabel: PieLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    index,
  }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    const renderIcon = () => {
      switch (index) {
        case 0:
          return <MarketingIcon />;
        case 1:
          return <DocumentControlIcon />;
        case 2:
          return <ProjectManagmentIcon />;
        case 3:
          return <ManagingContentIcon />;
        default:
          return null;
      }
    };

    return (
      <g className="career-icons">
        <foreignObject
          x={x - (matches ? 22 : 38)}
          y={y - (matches ? 20 : 33.5)}
          width="76"
          height="67"
        >
          {renderIcon()}
        </foreignObject>
      </g>
    );
  };

  if (!report || !report.key_numbers || !report.key_numbers) {
    return null;
  }

  const data = generateData(report.key_numbers.data);

  return (
    <Paper radius="24px" p="32px" mt={32} withBorder>
      <Title
        size={matches ? 24 : 36}
        mb={8}
        style={{ wordBreak: "break-word" }}
      >
        {report?.key_numbers?.mark_down
          ? report.key_numbers.mark_down.replace(/^## /, "")
          : "Career in Numbers"}
      </Title>
      <Text style={{ fontSize: matches ? 16 : 18 }} mb={24} c="dimmed">
        {report?.key_numbers?.explanation
          ? report?.key_numbers?.explanation
          : "Description"}
      </Text>
      <Paper radius="15px" p={matches ? 0 : 36} mb={40} withBorder={!matches}>
        <Flex
          justify="space-between"
          align="center"
          gap={32}
          direction={matches ? "column-reverse" : undefined}
        >
          <Group className="experience-text">
            <Stack gap={16}>
              <Text fw="bold" mb={8} size={matches ? "16px" : "24px"}>
                {report?.key_numbers?.overview
                  ? report?.key_numbers?.overview
                  : ""}
              </Text>
              {report?.key_numbers.data.map((item, index) => (
                <Flex align="center" gap={8} key={index}>
                  <ColorSwatch
                    color={COLORS[index % COLORS.length]}
                    size={16}
                  />
                  <Group gap={4}>
                    <Text size={matches ? "16px" : "24px"}>{item.title}</Text>
                  </Group>
                </Flex>
              ))}
            </Stack>
          </Group>
          <Box>
            <PieChart width={matches ? 279 : 416} height={matches ? 279 : 416}>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={matches ? 140 : 208}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
            </PieChart>
          </Box>
        </Flex>
      </Paper>
      <Paper radius="15px" p={matches ? 0 : 36} withBorder={!matches}>
        <Flex
          justify="space-between"
          align={matches ? "center" : "start"}
          ta="center"
          gap={matches ? 32 : 16}
          direction={matches ? "column" : undefined}
        >
          {/* Мапимо ролі */}
          {report.key_numbers.data.map((role, index) => (
            <Text
              maw={216}
              size={matches ? "16px" : "24px"}
              fw={matches ? "bold" : undefined}
              key={index}
            >
              <HoverCard key={index} width={280} shadow="md" position="top">
                <HoverCard.Target>
                  <div>
                    <Text
                      c={COLORS[index % COLORS.length]}
                      size={
                        role.title.split(" ")[0].length > 5
                          ? matches
                            ? "28px"
                            : "52px"
                          : matches
                          ? "36px"
                          : "64px"
                      }
                      fw={600}
                      style={{
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        lineHeight: 1.1,
                        maxWidth: "100%",
                      }}
                    >
                      {role.title.split(" ")[0]}
                    </Text>
                    <Text>{role.title.split(" ").slice(1).join(" ")}</Text>
                  </div>
                </HoverCard.Target>
                <HoverCard.Dropdown>
                  <Text key={index} maw={matches ? 199 : 208} mih={200}>
                    {role.description}
                  </Text>
                </HoverCard.Dropdown>
              </HoverCard>
            </Text>
          ))}
        </Flex>
      </Paper>
      <LikeResponse report_id={reportId} section="key_numbers" />
    </Paper>
  );
};
